import { TrackingLink } from './TrackingLink';
import { shipmentStatus } from 'containers/NestedContainers/data/orders-data';
import { OrderItemValues, OrderTracking } from 'types/order';

interface OrderShipmentSummaryProps {
  orderId: string;
  trackings?: OrderTracking[] | null;
  items?: OrderItemValues[];
}

const cleanText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const safeUrl = (value: unknown): URL | null => {
  try {
    const url = new URL(cleanText(value));
    return ['http:', 'https:'].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
};

const shipmentKeys = (tracking: OrderTracking): string[] => {
  const number = cleanText(tracking.trackingNumber);
  const url = safeUrl(tracking.defaultTrackingUrl);
  // An untrackable record must not hide a usable legacy link.
  if (!number && !url) return [];
  const keys = number ? [`number:${number}`] : [];
  if (url) {
    url.hash = '';
    keys.push(`url:${url.href}`);
  }
  return keys;
};

export const OrderShipmentSummary = ({
  orderId,
  trackings,
  items,
}: OrderShipmentSummaryProps) => {
  const shipments: OrderTracking[] = (trackings || []).filter(Boolean);
  const knownShipments = new Set(shipments.flatMap(shipmentKeys));
  (items || [])
    .filter((item) => cleanText(item.trackingUrl))
    .forEach((item) => {
      const tracking: OrderTracking = {
        trackingNumber: item.trackingId,
        defaultTrackingUrl: item.trackingUrl,
        estDeliveryDate: item.estimatedDeliveredAt,
        orderItem: { title: item.title, quantity: item.quantity },
      };
      const keys = shipmentKeys(tracking);
      if (!keys.some((key) => knownShipments.has(key))) {
        shipments.push(tracking);
        keys.forEach((key) => knownShipments.add(key));
      }
    });

  return (
    <section
      aria-label="Shipment tracking"
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">
          Shipment tracking
        </h2>
      </div>
      {shipments.length === 0 ? (
        <p className="px-5 py-4 text-sm text-gray-500">
          No shipment tracking is available yet.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm text-gray-700">
          {shipments.map((tracking, index) => {
            const number = cleanText(tracking.trackingNumber);
            const title = cleanText(tracking.orderItem?.title);
            const quantity = tracking.orderItem?.quantity;
            const status = shipmentStatus.find(
              (status) => status.value === tracking.trackerStatus,
            );
            const href = safeUrl(tracking.defaultTrackingUrl)?.href;
            const date =
              tracking.estDeliveryDate instanceof Date
                ? tracking.estDeliveryDate
                : cleanText(tracking.estDeliveryDate)
                ? new Date(cleanText(tracking.estDeliveryDate))
                : null;
            const validDate = date && Number.isFinite(date.getTime());
            const dateValue = validDate ? date!.toISOString() : '';
            const linkLabel = number
              ? `Track shipment ${number}`
              : title
              ? `Track shipment for ${title}`
              : 'Track shipment';
            const linkClassName =
              'shrink-0 rounded text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500';
            return (
              <li key={`${number}:${index}`} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <p className="min-w-0 break-all font-medium text-gray-900">
                    {number || 'Tracking number unavailable'}
                  </p>
                  {number ? (
                    <TrackingLink
                      trackingNumber={number}
                      ownerType="ORDER"
                      ownerId={orderId}
                      aria-label={linkLabel}
                      className={linkClassName}
                    >
                      Track
                    </TrackingLink>
                  ) : href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={linkLabel}
                      className={linkClassName}
                    >
                      Track
                    </a>
                  ) : (
                    <span className="shrink-0 text-gray-500">Unavailable</span>
                  )}
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                  <div className="min-w-0">
                    <dt className="text-xs text-gray-500">Status</dt>
                    <dd
                      className={`mt-1 break-words ${
                        status?.textColor || 'text-gray-500'
                      }`}
                    >
                      {status?.label || 'Status unavailable'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-gray-500">Item</dt>
                    <dd className="mt-1 break-words">
                      <p>{title || 'Item unavailable'}</p>
                      {typeof quantity === 'number' &&
                        Number.isFinite(quantity) &&
                        quantity > 0 && (
                          <p className="mt-1 text-xs text-gray-500">
                            Quantity: {quantity}
                          </p>
                        )}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-gray-500">Carrier</dt>
                    <dd className="mt-1 break-words">
                      {cleanText(tracking.trackingCompany) ||
                        'Carrier unavailable'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-gray-500">
                      Estimated delivery
                    </dt>
                    <dd className="mt-1 break-words">
                      {validDate ? (
                        <time dateTime={dateValue}>
                          {date!.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            timeZone: 'UTC',
                          })}
                        </time>
                      ) : (
                        'Not available'
                      )}
                    </dd>
                  </div>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
